import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Alert,
  Button
} from "@mui/material";

import CustomButton from "../Common/CustomButton";
import { MixpanelService } from "../../utils/mixpanelService";
import { createTestSuite } from "../../redux-store/testSuiteReducers/testSuiteActions";
import { attachScenariosWithSuites } from "../../redux-store/testSuiteScenarioReducers/testSuiteScenarioActions";
import coreUtils from "../../utils/coreUtils";

const CreateTestSuiteModal = ({ projectId, open, onClose, onCancel }) => {
  const dispatch = useDispatch();
  const selectedActiveTestScenarios = useSelector(
    (state) => state.testScenario.selectedTestScenarios.filter((test) => test.status === "active")
  );
  const [errors, setErrors] = useState({});
  const [title, setTitle] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (open) MixpanelService.track("create_test_suite_modal_opened", {});
  }, [open]);

  useEffect(() => {
    validate(["scenario"]);
  }, []);

  const handleChange = (event) => {
    setTitle(event.target.value);
  };

  const handleClose = () => {
    MixpanelService.track("create_test_suite_modal_closed", {});
    onClose?.();
  };

  const handleCancel = () => {
    MixpanelService.track("create_test_suite_modal_closed", {});
    setTitle("");
    setErrors({});
    onCancel?.();
  };

  const validate = (tests = ["scenario", "title"]) => {
    let isValid = true;
    if (
      (!selectedActiveTestScenarios || selectedActiveTestScenarios?.length === 0) &&
      tests.indexOf("scenario") !== -1
    ) {
      setErrors({ scenario: "Please select atleast one active test scenario." });
      isValid = false;
    }
    if (
      coreUtils.isStringInvalidOrBlank(title) &&
      tests.indexOf("title") !== -1
    ) {
      setErrors({ title: "Please enter a valid Title" });
      isValid = false;
    }
    return isValid;
  };

  const onCreateConfirm = async () => {
    setIsSaving(true);
    if (!validate()) {
      setIsSaving(false);
      return;
    }
    const reponse = await dispatch(
      createTestSuite({ projectId: projectId, title: title })
    );
    if (reponse?.payload?.id) {
      MixpanelService.track("test_suite_created", { test_suite_id: reponse.payload.id });
      await dispatch(
        attachScenariosWithSuites({
          testSuiteId: reponse.payload.id,
          testScenarioIds: [...selectedActiveTestScenarios.map((test) => test.id)],
        })
      );
      window.open(`/test-suites/${reponse.payload.id}/`, "_blank");
    } else {
      //toast with error message
    }
    setIsSaving(false);
    setTitle("");
    setErrors({});
    onCancel?.();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="attach-test-suite-dialog-title"
    >
      <DialogTitle id="attach-test-suite-dialog-title" sx={{ fontSize: 20 }}>
        Create new test suite
      </DialogTitle>
      <DialogContent
        sx={{
          width: "500px",
          height: "auto",
          maxHeight: "500px",
          overflow: "auto",
        }}
      >
        <span style={{ fontSize: "14px" }} className="text_caption_error">
          {errors.title || errors.scenario}
        </span>
        <h5 style={{ marginTop: 16 }}>Title</h5>
        <TextField
          sx={{ mt: 1, width: 1 }}
          size="small"
          variant="outlined"
          value={title}
          onChange={handleChange}
        />
        <h5 style={{ marginTop: 32, marginBottom: 8 }}>
          Selected Test Sceanrios
        </h5>
        {selectedActiveTestScenarios.map((test) => (
          <Chip
            key={test.id}
            id={test.id}
            label={test.title}
            size="small"
            sx={{ mr: 1, mb: 1 }}
          />
        ))}
        {!selectedActiveTestScenarios.length && <h6>None selected</h6>}
        <Alert severity="info" sx={{ mt: 2 }}>
          Only test scenarios with active status will be considered for test suite creation.
        </Alert>
      </DialogContent>
      <DialogActions sx={{ p: 3 }}>
        <Button
          onClick={handleCancel}
          variant="outlined"
          color="error"
          disabled={isSaving}
        >
          Cancel
        </Button>
        <Button
          onClick={onCreateConfirm}
          variant="contained"
          color="primary"
          disabled={
            coreUtils.isStringInvalidOrBlank(title) || !!errors.scenario
          }
        >
          Create new test suite
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateTestSuiteModal;
